import { useAppSelector } from "store/hooks";
import LoadingImage from "components/LoadingImage";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { APIGetMentors, APIGetMentorTransactions, APIGetMentorTransactionsSummary } from "utils/api";
import { Popover } from "@mui/material";
import { formatCurrency, formatRateFromBasisPointsToPercentage, formatValueFromIntToDecimalFromCurrency } from "utils/utils";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

const DetailsPopover = ({ anchorEl, onClose, details }) => {
  const open = Boolean(anchorEl);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      PaperProps={{
        sx: {
          p: 2,
          width: "350px",
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
          borderRadius: "10px",
        },
      }}
    >
      <div className="p-2">
        <h3 className="mb-3 text-lg font-medium text-[#111827]">More details</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">List Price</span>
            <span className="font-medium">{formatValueFromIntToDecimalFromCurrency(details?.listPrice, details?.currency)} {formatCurrency(details?.currency)}</span>
          </div>
          {/* delete all line if 0 */}
          {details?.discounts?.length > 0 && (
            details?.discounts?.map((discount) => (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount <em>{discount.code} {discount.percentage > 0 ? `(${discount.percentage}%)` : `(${formatValueFromIntToDecimalFromCurrency(discount.amount, details?.currency)} ${formatCurrency(details?.currency)})`}</em></span>
                  <span className="font-medium">-{formatValueFromIntToDecimalFromCurrency(discount.amount, details?.currency)} {formatCurrency(details?.currency)}</span>
                </div>
              </>
            ))
          )}
            <div className="flex justify-between">
              <span className="text-gray-600">Net Price</span>
            <span className="font-medium">{formatValueFromIntToDecimalFromCurrency(details?.netPrice, details?.currency)} {formatCurrency(details?.currency)}</span> 
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">VAT Collected <em>({formatRateFromBasisPointsToPercentage(details?.vatRate)}%)</em></span>
            <span className="font-medium">{formatValueFromIntToDecimalFromCurrency(details?.vatAmount, details?.currency)} {formatCurrency(details?.currency)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Net Revenue Excl. VAT</span>
            <span className="font-medium">{formatValueFromIntToDecimalFromCurrency(details?.amountExVAT, details?.currency)} {formatCurrency(details?.currency)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Platform Commission <em>({formatRateFromBasisPointsToPercentage(details?.platformCommissionRate)}%)</em></span>
            <span className="font-medium">{formatValueFromIntToDecimalFromCurrency(details?.platformCommission, details?.currency)} {formatCurrency(details?.currency)}</span>
          </div>
          <div className="flex justify-between font-semibold text-[#407EC9]">
            <span>Mentor Earnings</span>
            <span>{formatValueFromIntToDecimalFromCurrency(details?.mentorNet, details?.currency)} {formatCurrency(details?.currency)}</span>
          </div>
        </div>
      </div>
    </Popover>
  );
};

const FinancialsPage = () => {
  const user = useAppSelector((state) => state.auth.user_me);
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const navigate = useNavigate();

  const mentorQuery = useQuery({
    queryKey: ["mentor_me"],
    queryFn: () => APIGetMentors(null, true, user.id).then((res) => res.data[0] || null),
    enabled: !!isLoggedIn && !!user.id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (mentorQuery.data && !mentorQuery.data.is_active) {
      navigate("/error-404");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentorQuery.data]);

  const transactionsQuery = useQuery({
    queryKey: ["mentor_transactions", user?.id],
    queryFn: () => APIGetMentorTransactions(user.id).then(res => res.data),
    enabled: !!isLoggedIn && !!user.id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const summaryQuery = useQuery({
    queryKey: ["mentor_transactions_summary", user?.id],
    queryFn: () => APIGetMentorTransactionsSummary(user?.id).then(res => res.data),
    enabled: !!isLoggedIn && !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const formatPriceDate = (date: string) => {
    if (!date) return "";
    return format(new Date(date), "yyyy-MM-dd HH:mm");
  };

  return (
    <section className="flex w-full h-full border-l-[1px] border-[#F5F5F5]">
      {
        mentorQuery.isLoading || !user || !mentorQuery.data || !mentorQuery.data.is_active ? (
          <LoadingImage />
        ) : (
          <>
            <div className="flex flex-col pt-[56px] px-[46px] h-full w-full">
              <div className="flex flex-col items-start justify-between mb-8 md:flex-row md:items-center text-[#111827]">
                <div>
                  <h1 className="custom-text-[volkhov-700-35px]">
                    Hello {user.firstname}
                  </h1>
                  <p className="custom-text-[poppins-400-18px]">
                    View your financial informations here !
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 mb-10 md:grid-cols-2">
                <div className="p-[25px] rounded-[20px] border-[0.2px] border-[#407EC9] border-opacity-20 shadow-[0px_24px_32px_0px_rgba(0,0,0,0.04),_0px_16px_24px_0px_rgba(0,0,0,0.04),_0px_4px_8px_0px_rgba(0,0,0,0.04),_0px_0px_1px_0px_rgba(0,0,0,0.04)] text-[#111827]">
                  <p className="mb-[17px] custom-text-[poppins-400-18px] border-b-[1px] border-[#407EC9] border-opacity-20">Total earnings: {formatValueFromIntToDecimalFromCurrency(summaryQuery.data?.total_earnings, summaryQuery.data?.currency) ?? "..."} {formatCurrency(summaryQuery.data?.currency)}</p>
                  <h2 className="mb-[7px] custom-text-[poppins-700-30px]">Net earnings</h2>
                  <p className="text-[#407EC9] custom-text-[poppins-700-45px] leading-none">{formatValueFromIntToDecimalFromCurrency(summaryQuery.data?.net_earnings, summaryQuery.data?.currency) ?? "..."} {formatCurrency(summaryQuery.data?.currency)}</p>
                </div>
                <div className="p-[25px] rounded-[20px] border-[0.2px] border-[#407EC9] border-opacity-20 shadow-[0px_24px_32px_0px_rgba(0,0,0,0.04),_0px_16px_24px_0px_rgba(0,0,0,0.04),_0px_4px_8px_0px_rgba(0,0,0,0.04),_0px_0px_1px_0px_rgba(0,0,0,0.04)] text-[#111827]">
                  <p className="mb-[17px] custom-text-[poppins-400-18px] border-b-[1px] border-[#407EC9] border-opacity-20">Paid out: {formatValueFromIntToDecimalFromCurrency(summaryQuery.data?.total_paid_out, summaryQuery.data?.currency) ?? "..."} {formatCurrency(summaryQuery.data?.currency)}</p>
                  <h2 className="mb-[7px] custom-text-[poppins-700-30px]">Pending Balance</h2>
                  <p className="text-[#407EC9] custom-text-[poppins-700-45px] leading-none">{formatValueFromIntToDecimalFromCurrency(summaryQuery.data?.pending_balance, summaryQuery.data?.currency) ?? "..."} {formatCurrency(summaryQuery.data?.currency)}</p>
                </div>
              </div>

              <div className="mb-[108px]">
                <div className="bg-white rounded-[20px] shadow-[0px_4px_12px_0px_rgba(13,10,44,0.06)]">
                  <div className="mb-[29px] mt-[20px] ml-[20px] inline-flex justify-between items-center gap-[17px]">
                    <img src={require('assets/financials.svg').default} alt="financials" />
                    <h2 className="custom-text-[poppins-400-20px]">Transactions</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#EAF3FF] border-b !text-center">
                          <th className="px-6 py-[5px] text-[#6B7280] custom-text-[poppins-500-14px]">Date</th>
                        <th className="px-6 py-[5px] text-left text-[#6B7280] custom-text-[poppins-500-14px]">Type</th>
                        <th className="px-6 py-[5px] text-[#6B7280] custom-text-[poppins-500-14px]">Description</th>
                        <th className="px-6 py-[5px] text-[#6B7280] custom-text-[poppins-500-14px]">Net Price ({summaryQuery.data?.currency})</th>
                        <th className="px-6 py-[5px] text-[#6B7280] custom-text-[poppins-500-14px]">Mentor Earnings ({summaryQuery.data?.currency})</th>
                        <th className="px-6 py-[5px] text-[#6B7280] custom-text-[poppins-500-14px]">Status</th>
                        <th className="px-6 py-[5px] text-[#6B7280] custom-text-[poppins-500-14px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionsQuery.data && transactionsQuery.data.length > 0 ? (
                        transactionsQuery.data.map((transaction) => (
                          <tr key={transaction.id} className="text-center border-b hover:bg-gray-50">
                            <td className="px-6 py-3 text-[#111827] custom-text-[poppins-400-14px] min-w-[170px]">{formatPriceDate(transaction.session_date)}</td>
                            <td className="px-6 py-3 text-[#111827] custom-text-[poppins-400-14px] capitalize">{transaction.type}</td>
                            <td className="px-6 py-3 text-[#111827] custom-text-[poppins-400-14px] ">{transaction.description}</td>
                            <td className="px-6 py-3 text-[#111827] custom-text-[poppins-400-14px]">{formatValueFromIntToDecimalFromCurrency(transaction.net_price, transaction.currency)}</td>
                            <td className="px-6 py-3 text-[#111827] custom-text-[poppins-400-14px] h-full items-center gap-[10px] capitalize">{
                              <div className="flex items-center gap-[10px] justify-center">
                                {transaction.type === "payout" ? (<img width={24} height={24} src={require('assets/wallet.svg').default} alt="payout" />) : null}
                                {formatValueFromIntToDecimalFromCurrency(transaction.mentor_net, transaction.currency)}
                              </div>}
                            </td>
                            <td className="px-6 py-3 custom-text-[poppins-400-14px]">
                              <span className="px-2 py-1 text-xs capitalize rounded-full">
                                {transaction.status}
                              </span>
                            </td>

                            <td className="px-6 py-3 custom-text-[poppins-400-14px]">
                              <button
                                className="text-gray-400 hover:text-gray-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAnchorEl(e.currentTarget);
                                  setSelectedTransaction({
                                    details: {
                                      listPrice: transaction.list_price,
                                      netPrice: transaction.net_price,
                                      discounts: transaction.discounts,
                                      vatRate: transaction.vat_rate,
                                      vatAmount: transaction.vat_amount,
                                      platformFee: transaction.platform_fee,
                                      mentorNet: transaction.mentor_net,
                                      currency: transaction.currency,
                                      status: transaction.status,
                                      type: transaction.type,
                                      sessionDate: transaction.session_date,
                                      amountExVAT: transaction.amount_ex_vat,
                                      platformCommissionRate: transaction.platform_fee_rate,
                                      platformCommission: transaction.platform_fee,
                                    }
                                  });
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="py-4 text-center text-[#6B7280]">
                            No transactions found.
                          </td>
                        </tr>
                      )}
                      </tbody>
                    </table>
                  </div> {/* End of scroll wrapper */}
                </div>
              </div>

              {/* CTA Section */}
              {/* <div className="p-8 rounded-xl">
                <h2 className="text-2xl font-semibold mb-4 custom-text-[poppins-600-24px]">CTA / suggestion : learn how to boost your services</h2>
              </div> */}
            </div>

            {/* Details Popover */}
            <DetailsPopover
              anchorEl={anchorEl}
              onClose={handleClosePopover}
              details={selectedTransaction?.details}
            />
          </>
        )
      }
    </section>
  );
};

export default FinancialsPage;
